import { useState } from 'react'
import { X, Send, MessageCircle, Users } from 'lucide-react'
import { collection, serverTimestamp, getDocs, writeBatch, doc, setDoc } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/store/authStore'
import toast from 'react-hot-toast'

interface SendNotificationModalProps {
    isOpen: boolean
    onClose: () => void
    userId?: string
    userName?: string
    userPhone?: string
}

export default function SendNotificationModal({ isOpen, onClose, userId, userName, userPhone }: SendNotificationModalProps) {
    const { currentOrg } = useAuthStore()
    const [title, setTitle] = useState('')
    const [message, setMessage] = useState('')
    const [imageUrl, setImageUrl] = useState('')
    const [sending, setSending] = useState(false)

    const isBroadcast = userId === 'ALL'

    if (!isOpen) return null

    const handleSendPush = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!userId || !currentOrg) return

        setSending(true)
        try {
            if (isBroadcast) {
                // Fetch all users and send notification to their personal feed (Mass Push)
                const usersSnapshot = await getDocs(collection(db, 'users'))
                const batch = writeBatch(db)
                let count = 0

                usersSnapshot.docs.forEach((userDoc) => {
                    const newNotifRef = doc(collection(db, 'users', userDoc.id, 'notifications'))
                    batch.set(newNotifRef, {
                        userId: userDoc.id,
                        orgId: currentOrg.id,
                        title,
                        message,
                        imageUrl: imageUrl || null,
                        read: false,
                        createdAt: serverTimestamp(),
                        type: 'ADMIN_MESSAGE'
                    })
                    count++
                })

                await batch.commit()
                toast.success(`Notificação enviada para ${count} usuários!`)
            } else {
                // Single user
                const newNotifRef = doc(collection(db, 'users', userId, 'notifications'))
                await setDoc(newNotifRef, {
                    userId,
                    orgId: currentOrg.id,
                    title,
                    message,
                    imageUrl: imageUrl || null,
                    read: false,
                    createdAt: serverTimestamp(),
                    type: 'ADMIN_MESSAGE'
                })
                toast.success('Notificação enviada com sucesso!')
            }

            onClose()
            setTitle('')
            setMessage('')
        } catch (error) {
            console.error('Erro ao enviar notificação:', error)
            toast.error('Erro ao enviar.')
        } finally {
            setSending(false)
        }
    }

    const handleSendWhatsApp = () => {
        if (!userPhone) {
            toast.error('Usuário sem telefone cadastrado')
            return
        }
        // Remove non-digits
        const cleanPhone = userPhone.replace(/\D/g, '')

        // Simple validation logic for Brazil numbers
        let finalPhone = cleanPhone
        if (cleanPhone.length >= 10 && cleanPhone.length <= 11) {
            // If number is just (11) 99999-9999, add 55
            finalPhone = `55${cleanPhone}`
        }

        const url = `https://wa.me/${finalPhone}?text=${encodeURIComponent(message || `Olá ${userName}, tenho um comunicado sobre sua reserva.`)}`
        window.open(url, '_blank')
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-[2.5rem] w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="bg-gray-50 px-8 py-6 border-b border-gray-100 flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-black text-gray-900 uppercase italic tracking-tighter">
                            {isBroadcast ? 'Mensagem em Massa' : 'Nova Mensagem'}
                        </h2>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1 flex items-center gap-1">
                            Para: <span className="text-primary">{userName || 'Usuário'}</span>
                            {isBroadcast && <Users className="w-3 h-3 text-primary ml-1" />}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <X className="w-5 h-5 text-gray-400" />
                    </button>
                </div>

                <div className="p-8 space-y-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Título</label>
                        <input
                            type="text"
                            className="w-full bg-gray-50 border-2 border-slate-100 focus:bg-white focus:border-primary rounded-xl px-4 py-3 text-sm font-bold outline-none transition-all"
                            placeholder="Ex: Aviso Importante"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Mensagem</label>
                        <textarea
                            className="w-full bg-gray-50 border-2 border-slate-100 focus:bg-white focus:border-primary rounded-xl px-4 py-3 text-sm font-medium outline-none transition-all min-h-[120px] resize-none"
                            placeholder="Digite sua mensagem aqui..."
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Imagem URL (Opcional)</label>
                        <input
                            type="text"
                            className="w-full bg-gray-50 border-2 border-slate-100 focus:bg-white focus:border-primary rounded-xl px-4 py-3 text-sm font-bold outline-none transition-all"
                            placeholder="https://..."
                            value={imageUrl}
                            onChange={(e) => setImageUrl(e.target.value)}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        {!isBroadcast && (
                            <button
                                type="button"
                                onClick={handleSendWhatsApp}
                                disabled={!userPhone}
                                className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black py-4 rounded-xl shadow-lg shadow-green-500/20 transform active:scale-95 transition-all uppercase tracking-wider text-xs"
                            >
                                <MessageCircle className="w-4 h-4" />
                                WhatsApp
                            </button>
                        )}
                        <button
                            onClick={handleSendPush}
                            disabled={sending || !title || !message}
                            className={`flex items-center justify-center gap-2 bg-primary hover:bg-primary-600 disabled:bg-gray-100 disabled:text-gray-300 text-white font-black py-4 rounded-xl shadow-lg shadow-primary/20 transform active:scale-95 transition-all uppercase tracking-wider text-xs ${isBroadcast ? 'col-span-2' : ''}`}
                        >
                            {sending ? (
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <>
                                    <Send className="w-4 h-4" />
                                    {isBroadcast ? 'Enviar para Todos' : 'Enviar Push'}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
